import type { CompanyProfile, TenantContractStatus } from '@felix/domain';
import { formatDay, Panel, StatusBadge, type StatusTone } from '@felix/ui';
import { Skeleton, Text } from '@mantine/core';
import { IconBuilding } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, officeApi } from '../api.js';
import { ErrorAlert } from './ErrorAlert.js';

const TONE: Record<TenantContractStatus, StatusTone> = {
  none: 'neutral',
  scheduled: 'info',
  active: 'success',
  expiring: 'warning',
  expired: 'danger',
};

type Info = Awaited<ReturnType<typeof officeApi.companyInfo>>;

/**
 * Ayarlar ▸ Şirket bilgileri (ADR 0140 §4; yalnız web, yalnız şirket yöneticisi): Felixmate'teki
 * şirket kaydı (adres, vergi, irtibat, yetkili kişi) ve aboneliğin bitişi ile kalan gün. Salt
 * okunur; değişiklik panelden yapılır.
 */
export function CompanyInfoCard() {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; data: Info }
  >({ kind: 'loading' });
  useEffect(() => {
    officeApi
      .companyInfo()
      .then((data) => setState({ kind: 'ready', data }))
      .catch((failure: unknown) =>
        setState({ kind: 'error', code: failure instanceof ApiError ? failure.code : 'GENERIC' }),
      );
  }, []);
  const regions = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.resolvedLanguage ?? 'en'], { type: 'region' });
    } catch {
      return null;
    }
  }, [i18n.resolvedLanguage]);

  const body = () => {
    if (state.kind === 'loading') return <Skeleton height={120} />;
    if (state.kind === 'error') return <ErrorAlert code={state.code} />;
    const { profile, contract } = state.data;
    const p: Partial<CompanyProfile> = profile ?? {};
    const address = [
      p.addressLine1,
      p.addressLine2,
      [p.postalCode, p.city].filter(Boolean).join(' '),
      p.country ? (regions?.of(p.country) ?? p.country) : null,
    ].filter(Boolean);
    const rows: Array<[string, string | null | undefined]> = [
      [t('settings.companyInfo.legalName'), p.legalName],
      [t('settings.companyInfo.address'), address.join(', ')],
      [t('settings.companyInfo.tax'), [p.taxOffice, p.taxNumber].filter(Boolean).join(' · ')],
      [t('settings.companyInfo.phone'), p.phone],
      [t('settings.companyInfo.email'), p.email],
      [t('settings.companyInfo.website'), p.website],
      [
        t('settings.companyInfo.person'),
        [p.contactName, p.contactTitle, p.contactPhone, p.contactEmail].filter(Boolean).join(' · '),
      ],
    ];
    return (
      <>
        {profile ? (
          <dl style={{ margin: 0, display: 'grid', gap: 6 }}>
            {rows.map(([label, value]) => (
              <div key={label} style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <dt style={{ minWidth: 160, color: 'var(--felix-text-secondary)' }}>{label}</dt>
                <dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{value || '—'}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <Text fz="sm" c="var(--felix-text-secondary)">
            {t('settings.companyInfo.empty')}
          </Text>
        )}
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
            marginTop: 12,
          }}
        >
          <Text fz="sm" fw={600}>
            {t('settings.companyInfo.contract')}
          </Text>
          <StatusBadge tone={TONE[contract.status]} variant="pill">
            {t(`settings.companyInfo.status.${contract.status}`)}
          </StatusBadge>
          {contract.endsOn ? (
            <Text fz="sm">
              {t('settings.companyInfo.endsOn', { date: formatDay(contract.endsOn) })}
              {contract.daysLeft !== null && contract.daysLeft >= 0
                ? ` · ${t('settings.companyInfo.daysLeft', { count: contract.daysLeft })}`
                : ''}
            </Text>
          ) : null}
        </div>
        <Text fz="xs" c="var(--felix-text-secondary)" mt="sm">
          {t('settings.companyInfo.readOnly')}
        </Text>
      </>
    );
  };

  return (
    <Panel
      title={t('settings.companyInfo.title')}
      icon={<IconBuilding size={18} stroke={1.8} />}
      divider
    >
      {body()}
    </Panel>
  );
}
